import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Users, Crown, Gift, UserPlus, Search, Check, X, Award, Loader2, Trash2 } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AdminStatsGrid } from "./AdminStatsGrid";

export const AdminUsersTab = ({ stats, users, foundersList, accessGrants, token, onRefresh }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [grantEmail, setGrantEmail] = useState("");
  const [grantType, setGrantType] = useState("lifetime");
  const [isGranting, setIsGranting] = useState(false);
  const [founderEmail, setFounderEmail] = useState("");
  const [founderNumber, setFounderNumber] = useState("");
  const [isAssigningFounder, setIsAssigningFounder] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const handleGrantAccess = async () => {
    if (!grantEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    setIsGranting(true);
    try {
      await axios.post(`${API}/admin/grant-access`, { email: grantEmail, access_type: grantType }, authHeaders);
      toast.success(`${grantType === 'lifetime' ? 'Lifetime scholarship' : 'Beta'} access granted to ${grantEmail}`);
      setGrantEmail("");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant access");
    } finally {
      setIsGranting(false);
    }
  };

  const handleAssignFounder = async () => {
    if (!founderEmail || !founderNumber) {
      toast.error("Please enter both email and founder number");
      return;
    }
    const num = parseInt(founderNumber);
    if (isNaN(num) || num < 1 || num > 100) {
      toast.error("Founder number must be between 1 and 100");
      return;
    }
    setIsAssigningFounder(true);
    try {
      const response = await axios.post(`${API}/admin/assign-founder`, { email: founderEmail, founder_number: num }, authHeaders);
      toast.success(`🎉 ${response.data.message}`);
      setFounderEmail("");
      setFounderNumber("");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign founder status");
    } finally {
      setIsAssigningFounder(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete "${userName}"?\n\nThis will remove all their data permanently.`)) {
      return;
    }
    setDeletingUserId(userId);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, authHeaders);
      toast.success(`User "${userName}" deleted successfully`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeletingUserId(null);
    }
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <AdminStatsGrid stats={stats} />

      {/* Grant Access Section */}
      <Card className="glass-card rounded-3xl border-0 mb-8">
        <CardContent className="p-6">
          <h2 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary" />
            Grant Special Access
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              type="email"
              placeholder="Enter email address"
              value={grantEmail}
              onChange={(e) => setGrantEmail(e.target.value)}
              className="flex-1 rounded-xl"
            />
            <select
              value={grantType}
              onChange={(e) => setGrantType(e.target.value)}
              className="px-4 py-2 rounded-xl bg-muted border-0 text-foreground"
            >
              <option value="lifetime">Lifetime Scholarship</option>
              <option value="beta">Beta Tester (3 months)</option>
            </select>
            <Button onClick={handleGrantAccess} disabled={isGranting} className="btn-primary whitespace-nowrap">
              {isGranting ? "Granting..." : "Grant Access"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Access will be applied when the user signs up with this email, or immediately if they already have an account.
          </p>
        </CardContent>
      </Card>

      {/* Assign Founder Status Section */}
      <Card className="glass-card rounded-3xl border-0 mb-8 border-l-4 border-l-amber-500">
        <CardContent className="p-6">
          <h2 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            Assign Founder Status
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Manually assign Founder's Circle status to a paid subscriber. Use this to fix subscriptions that were processed before the webhook update.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              type="email"
              placeholder="Subscriber's email"
              value={founderEmail}
              onChange={(e) => setFounderEmail(e.target.value)}
              className="flex-1 rounded-xl"
              data-testid="founder-email-input"
            />
            <Input
              type="number"
              placeholder="Founder #"
              min="1"
              max="100"
              value={founderNumber}
              onChange={(e) => setFounderNumber(e.target.value)}
              className="w-32 rounded-xl"
              data-testid="founder-number-input"
            />
            <Button
              onClick={handleAssignFounder}
              disabled={isAssigningFounder}
              className="bg-amber-500 hover:bg-amber-600 text-white whitespace-nowrap"
              data-testid="assign-founder-btn"
            >
              {isAssigningFounder ? "Assigning..." : "👑 Assign Founder"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            This will update their subscription, user record, and award the Founder's Circle badge.
          </p>
        </CardContent>
      </Card>

      {/* Founders List */}
      <Card className="glass-card rounded-3xl border-0 mb-8">
        <CardContent className="p-6">
          <h2 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            Founding Members
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            First 100 members with lifetime legacy status and 25% off all plans.
          </p>

          {foundersList.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Badge</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Name</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Email</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Access</th>
                  </tr>
                </thead>
                <tbody>
                  {foundersList.map((founder) => (
                    <tr key={founder.email} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-3 px-2">
                        <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                          <Award className="w-4 h-4" />
                          #{founder.founder_number}
                        </span>
                      </td>
                      <td className="py-3 px-2 font-medium text-foreground">{founder.name}</td>
                      <td className="py-3 px-2 text-muted-foreground">{founder.email}</td>
                      <td className="py-3 px-2">
                        {founder.is_registered ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 px-2 py-1 rounded-full">
                            <Check className="w-3 h-3" /> Registered
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 px-2 py-1 rounded-full">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-teal-700 dark:bg-emerald-900/30 dark:text-teal-300 px-2 py-1 rounded-full">
                          <Crown className="w-3 h-3" /> {founder.access_type === 'lifetime' ? 'Lifetime' : founder.access_type}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">No founders yet</p>
          )}

          <p className="text-xs text-muted-foreground mt-4">
            {100 - foundersList.length} founding spots remaining
          </p>
        </CardContent>
      </Card>

      {/* Pre-Granted Access */}
      {accessGrants.length > 0 && (
        <Card className="glass-card rounded-3xl border-0 mb-8">
          <CardContent className="p-6">
            <h2 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Gift className="w-5 h-5 text-emerald-500" />
              Pre-Granted Access
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              These emails will receive special access when they sign up:
            </p>
            <div className="space-y-2">
              {accessGrants.map((grant) => (
                <div key={grant.email} className="flex items-center justify-between py-2 border-b border-border/50">
                  <div>
                    <span className="text-foreground font-medium">{grant.email}</span>
                    {grant.name && <span className="text-muted-foreground ml-2">({grant.name})</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {grant.founder_number && (
                      <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-1 rounded-full">
                        Founder #{grant.founder_number}
                      </span>
                    )}
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      grant.access_type === 'lifetime'
                        ? 'bg-emerald-100 text-teal-700 dark:bg-emerald-900/30 dark:text-teal-300'
                        : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                    }`}>
                      {grant.access_type === 'lifetime' ? 'Lifetime' : 'Beta'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      <Card className="glass-card rounded-3xl border-0">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-lg font-semibold text-foreground flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              All Users
            </h2>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-xl"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">#</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Name</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Email</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Signed Up</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Quiz</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="border-b border-border/50 hover:bg-muted/30">
                    <td className="py-3 px-2">
                      {u.user_number <= 100 && (
                        <span className="text-amber-600 font-medium">#{u.user_number}</span>
                      )}
                    </td>
                    <td className="py-3 px-2 font-medium text-foreground">{u.name}</td>
                    <td className="py-3 px-2 text-muted-foreground">{u.email}</td>
                    <td className="py-3 px-2">
                      {u.subscription_type === 'lifetime' && (
                        <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-teal-700 dark:bg-emerald-900/30 dark:text-teal-300 px-2 py-1 rounded-full">
                          <Crown className="w-3 h-3" /> Lifetime
                        </span>
                      )}
                      {u.subscription_type === 'beta' && (
                        <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 px-2 py-1 rounded-full">
                          <Gift className="w-3 h-3" /> Beta
                        </span>
                      )}
                      {u.subscription_type === 'monthly' && (
                        <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full">
                          Monthly
                        </span>
                      )}
                      {u.subscription_type === 'annual' && (
                        <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full">
                          Annual
                        </span>
                      )}
                      {!u.subscription_type && (
                        <span className="text-xs text-muted-foreground">Free</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-sm text-muted-foreground">
                      {u.created_at?.slice(0, 10)}
                    </td>
                    <td className="py-3 px-2">
                      {u.quiz_completed ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <X className="w-4 h-4 text-muted-foreground" />
                      )}
                    </td>
                    <td className="py-3 px-2">
                      {u.email !== 'julie@jvdh.au' && !u.is_official_founder && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUser(u.id, u.name)}
                          disabled={deletingUserId === u.id}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                          data-testid={`delete-user-${u.id}`}
                        >
                          {deletingUserId === u.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredUsers.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No users found</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pending Access Grants */}
      {stats?.pending_grants?.length > 0 && (
        <Card className="glass-card rounded-3xl border-0 mt-6">
          <CardContent className="p-6">
            <h2 className="font-heading text-lg font-semibold text-foreground mb-4">
              Pending Access Grants
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              These emails will receive special access when they sign up:
            </p>
            <div className="space-y-2">
              {stats.pending_grants.map((grant) => (
                <div key={grant.email} className="flex items-center justify-between py-2 border-b border-border/50">
                  <span className="text-foreground">{grant.email}</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    grant.type === 'lifetime'
                      ? 'bg-emerald-100 text-teal-700 dark:bg-emerald-900/30 dark:text-teal-300'
                      : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                  }`}>
                    {grant.type === 'lifetime' ? 'Lifetime Scholarship' : 'Beta Tester'}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};
